import React, { useContext, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowForward } from "@mui/icons-material";
import { AuthContext } from "../context/AuthContext";
import { baseURL } from "../Constants";
import GamesBackLink from "../components/GamesBackLink";
import GameBoardRows from "../components/games/GameBoardRows";
import { GAME_BOARDS, boardConfig, parseVariant } from "../components/games/gameBoards";
import { useBadges } from "../services/BadgeService";
import { BoardPeriod, GameBoard, getGameLeaderboard } from "../services/MinigameService";
import { track } from "../utils/track";
import styles from "./GamesLeaderboardPage.module.css";

const BOARD_ROWS = 50;

/** /games/leaderboard — every game's board, one at a time; the selection lives in the URL. */
const GamesLeaderboardPage: React.FC = () => {
    const { t } = useTranslation("pages");
    const { user } = useContext(AuthContext);
    const badges = useBadges();
    const [params, setParams] = useSearchParams();

    const config = boardConfig(params.get("game")) ?? GAME_BOARDS[0];
    const selected = parseVariant(config, params.get("variant"));
    const variant = selected.join("-");
    const period: BoardPeriod = params.get("period") === "all" ? "all" : "month";

    const [board, setBoard] = useState<GameBoard | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => { track("games-leaderboard-open"); }, []);

    useEffect(() => {
        let cancelled = false;
        setBoard(null);
        setFailed(false);
        getGameLeaderboard(config.game, variant, period, BOARD_ROWS).then((b) => {
            if (cancelled) return;
            setBoard(b);
            setFailed(!b);
        });
        return () => { cancelled = true; };
    }, [config.game, variant, period, user?.steamID]);

    const select = (next: { game?: string; variant?: string; period?: BoardPeriod }) => {
        const game = next.game ?? config.game;
        // A different game starts on its own first variant.
        const nextVariant = next.variant ?? (next.game && next.game !== config.game ? undefined : variant);
        const search: Record<string, string> = { game, period: next.period ?? period };
        if (nextVariant) search.variant = nextVariant;
        setParams(search, { replace: true });
    };

    return (
        <div className={styles.page}>
            <GamesBackLink />

            <div className={styles.header}>
                <h1 className={styles.title}>{t("gamesLeaderboard.title")}</h1>
                <p className={styles.subtitle}>{t("gamesLeaderboard.subtitle")}</p>
            </div>

            {/* ===== Game tabs ===== */}
            <div className={styles.tabs} role="tablist">
                {GAME_BOARDS.map((g) => {
                    const active = g.game === config.game;
                    return (
                        <button
                            key={g.game}
                            role="tab"
                            aria-selected={active}
                            className={`${styles.tab} ${active ? styles.tabActive : ""}`}
                            style={active ? { borderBottomColor: g.accent } : undefined}
                            onClick={() => select({ game: g.game })}
                        >
                            {t(`gamesHub.games.${g.key}.title`)}
                        </button>
                    );
                })}
            </div>

            {/* ===== Controls ===== */}
            <div className={styles.controls}>
                <div className={styles.toggles}>
                    {config.dimensions.map((dim, i) => dim.length > 1 && (
                        <div key={i} className={styles.toggleGroup}>
                            {dim.map((o) => (
                                <button
                                    key={o.value}
                                    className={styles.toggleBtn}
                                    style={o.value === selected[i] ? { background: config.accent, color: config.accentInk } : undefined}
                                    onClick={() => select({ variant: selected.map((v, j) => (j === i ? o.value : v)).join("-") })}
                                >
                                    {t(o.labelKey)}
                                </button>
                            ))}
                        </div>
                    ))}
                    <div className={styles.toggleGroup}>
                        {(["month", "all"] as BoardPeriod[]).map((p) => (
                            <button
                                key={p}
                                className={`${styles.toggleBtn} ${p === period ? styles.toggleBtnActive : ""}`}
                                onClick={() => select({ period: p })}
                            >
                                {t(p === "month" ? "gamesLeaderboard.thisMonth" : "gamesLeaderboard.allTime")}
                            </button>
                        ))}
                    </div>
                </div>
                <div className={styles.meta}>
                    {board && board.totalPlayers > 0 && (
                        <span className={styles.count}>{t("gamesLeaderboard.players", { count: board.totalPlayers })}</span>
                    )}
                    <Link to={config.link} className={styles.playBtn}>
                        {t("gamesLeaderboard.play")}
                        <ArrowForward sx={{ fontSize: 17 }} />
                    </Link>
                </div>
            </div>

            {config.noteKey && <p className={styles.boardNote}>{t(config.noteKey)}</p>}

            {/* ===== Board ===== */}
            <div className={styles.card}>
                <div className={styles.columns}>
                    <span className={styles.colRank}>#</span>
                    <span className={styles.colPlayer}>{t("gamesLeaderboard.player")}</span>
                    <span>{t(config.scoreLabelKey)}</span>
                </div>
                {failed && <div className={styles.note}>{t("gamesLeaderboard.error")}</div>}
                {!failed && !board && <div className={styles.note}>{t("gamesLeaderboard.loading")}</div>}
                {board && board.entries.length === 0 && <div className={styles.note}>{t("gamesLeaderboard.empty")}</div>}
                {board && board.entries.length > 0 && (
                    <GameBoardRows config={config} entries={board.entries} me={board.me} badges={badges} avatarSize={28} />
                )}
            </div>

            {!user && (
                <div className={styles.loginNudge}>
                    <span>{t("gamesLeaderboard.loginNudge")}</span>
                    <button className={styles.loginBtn} onClick={() => { window.location.href = baseURL + "/Auth/steam"; }}>
                        {t("gamesLeaderboard.login")}
                    </button>
                </div>
            )}
        </div>
    );
};

export default GamesLeaderboardPage;
